export type EventButtonType = "INSCRIPTION" | "CUSTOM_LINK" | "NONE";

export interface EventFetch {
  id?: number;
  name: string;
  eventTypeId: number;
  eventSubTypeId: number;
  description: string;
  duration: string;
  guests: number;
  amount: number;
  price?: number | null;
  inscription: number;
  active: boolean;
  startDate: string;
  address: string;
  space: string;
  rules?: string | null;
  imageFormat?: string;
  imageBase64?: string;
  imageUrls?: string[];
  endDate?: string | null;
  isRecurring?: boolean;
  tenantId?: string | null;
  importantMessage?: string | null;
  hostAssists: boolean;
  paymentType?: "FREE" | "GRUPS" | "OTHER";
  venueId?: number | null;
  registrationDeadline?: number | null;
  featured?: boolean;
  featuredOrder?: number | null;
  inscriptionStartDate: string;
  suppressNotifications?: boolean;
  suppressRatingPrompt?: boolean;
  recurrenceSignupWeeks?: number | null;
  buttonType?: EventButtonType;
  buttonLabel?: string | null;
  buttonUrl?: string | null;
  showAttendeesInfo?: boolean;
  buttonSendSession?: boolean;
  buttonOpenInApp?: boolean;
  /** PTS-003 — Puntos extra al confirmarse la asistencia. null = el evento no da bonus. */
  bonusPoints?: number | null;
}

export interface Event extends EventFetch {
  id: number;
  deactivatedAt?: string | null;
  /** PTS-043 — El evento ya tiene check-ins: cambiar el bonus no los recalcula. */
  hasCheckins?: boolean;
  recurrenceGroupId?: number | string | null;
  eventTypeName?: string;
  eventSubTypeName?: string;
  available?: number;
  hostId?: number | null;
  hostFirstName?: string | null;
  hostLastName?: string | null;
  hostDescription?: string | null;
  hostImageFormat?: string | null;
  hostImageBase64?: string | null;
  users?: number;
  fictional?: number;
  eventType?: {
    id: number;
    name: string;
    subType: {
      id: number;
      name: string;
    };
  };
  host?: {
    id: number;
    firstName: string;
    lastName: string;
    description: string;
  };
  images?: Array<{
    id: number;
    url: string;
    format: string;
  }>;
  currentParticipants?: number;
  rating?: {
    average: number;
    count: number;
  };
  createdAt?: string;
  updatedAt?: string;
}

export interface EventUsers {
  paid: number;
  userId: number;
  eventUserId: number;
  userEmail: string;
  userFirstName: string;
  userLastName: string;
  userTypeId: number;
  userTypeName: string;
  phoneCode: string;
  phone: number;
  banned?: boolean;
  guests?: number;
  isHost?: boolean;
  companions?: Array<{ id: string; firstName: string; lastName: string }>;
}

export type EventStatusParam = "active" | "inactive" | "finished";

export interface EventsParams {
  id?: number;
  name?: string;
  startDate?: string;
  endDate?: string;
  typeId?: number;
  subTypeId?: number;
  userId?: number;
  tenantId?: string | null;
  limit?: number;
  page?: number;
  show?: string;
  featured?: boolean;
  eventStatus?: EventStatusParam;
  sortBy?: "startDate";
  sortOrder?: "asc" | "desc";
}

export interface EventsPagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface PaginatedEventsResponse {
  events: Event[];
  pagination?: EventsPagination;
}

export interface NewEventMessage {
  eventId: number;
  userId: number;
  title: string;
  message: string;
}

export interface EventMessage {
  id: number;
  title: string;
  message: string;
  createDate: string;
}

export interface UpdateEventMessage {
  id: number;
  title: string;
  message: string;
}

export interface EventSubTypeFetch {
  eventTypeId: number;
  eventSubTypeName: string;
}

export interface UpdateEventSubType {
  eventSubTypeId: number;
  eventTypeId: number;
  eventSubTypeName: string;
  tenantId?: string | null;
}

export interface NewEventSubType {
  eventSubTypeName: string;
  eventTypeId: number;
  tenantId?: string | null;
}

export interface NewEventType {
  name: string;
  tenantId?: string | null;
}

export interface UpdateEventType {
  id: number;
  name: string;
  tenantId?: string | null;
}

export interface EventTaxonomyInUsePlace {
  field: string;
  message: string;
}

export interface EventSubType {
  eventSubTypeId: number;
  eventSubTypeName: string;
  eventTypeId: number;
  eventTypeName?: string;
  createDate: string;
  tenantId?: string | null;
  inUse?: boolean;
  inUsePlaces?: EventTaxonomyInUsePlace[];
}

export interface EventType {
  eventTypeId: number;
  eventTypeName: string;
  subTypes: EventSubType[];
  tenantId?: string | null;
  inUse?: boolean;
  inUsePlaces?: EventTaxonomyInUsePlace[];
}

export interface EventTypeCatalogUsedEvent {
  id: number;
  name: string;
  startDate: string;
  tenantId: string | null;
}

export interface EventTypeDetail extends EventType {
  usedInEvents: EventTypeCatalogUsedEvent[];
}

export interface EventSubTypeDetail extends EventSubType {
  updatedAt: string | null;
  usedInEvents: EventTypeCatalogUsedEvent[];
}

export const EVENT_TAXONOMY_NAME_MAX_LENGTH = 100;

export interface EventCancellationReason {
  id: number;
  text: string;
}

export interface EventCancellationUser {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
}

export interface EventCancellation {
  id: number;
  cancelledAt: string;
  user: EventCancellationUser;
  reason: EventCancellationReason | null;
  customReason: string | null;
}

export interface EventCancellationsResponse {
  eventId: number;
  cancellations: EventCancellation[];
}

export interface EventInterestUser {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  createdAt: string;
}

export interface EventInterestPagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface EventInterestParams {
  id: number | string;
  page?: number;
  limit?: number;
  firstName?: string;
  lastName?: string;
  email?: string;
}

export interface EventInterestResponse {
  items: EventInterestUser[];
  pagination: EventInterestPagination;
}
